import React, { useState, useRef, useEffect } from 'react';

interface SearchBarProps {
  onSearch: (query: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Lista sugestii populare (în mod real ar veni din backend)
  const popularSearches = [
    'Literatura clasică', 'Science Fiction', 'Psihologie', 
    'Dostoievski', 'Fantasy', 'Dezvoltare personală',
    'Shakespeare', 'Management', 'Istorie'
  ];

  useEffect(() => {
    // Filtrează sugestiile pe baza comenzii
    if (query.length > 1) {
      const filtered = popularSearches.filter(item => 
        item.toLowerCase().includes(query.toLowerCase())
      );
      setSuggestions(filtered.slice(0, 5)); // Limitează la 5 sugestii
    } else {
      setSuggestions([]);
    }
  }, [query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(query);
    inputRef.current?.blur(); // Închide tastatura pe mobil după căutare
    setSuggestions([]); // Ascunde sugestiile după căutare
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    onSearch(suggestion);
    setSuggestions([]);
  };

  return (
    <div className="w-full max-w-3xl mx-auto mt-6 mb-8 relative">
      <form 
        onSubmit={handleSubmit} 
        className={`flex items-center transition-all duration-300 bg-white rounded-full overflow-hidden shadow-md ${isFocused ? 'shadow-lg ring-2 ring-blue-300' : ''}`}
      >
        <div className="pl-5 pr-3 text-gray-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder="Caută cărți, autori, categorii..."
          className="flex-1 py-3 px-3 outline-none text-gray-700"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
        />
        {query && (
          <button
            type="button"
            className="px-3 text-gray-400 hover:text-gray-600 transition"
            onClick={() => setQuery('')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-r-full transition-colors focus:outline-none"
        >
          Caută
        </button>
      </form>

      {/* Sugestii de căutare */}
      {suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100">
          <ul>
            {suggestions.map((suggestion, index) => (
              <li 
                key={index}
                className="cursor-pointer px-4 py-3 hover:bg-blue-50 transition text-gray-700 border-b border-gray-100 last:border-0"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {suggestion}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="mt-2 flex flex-wrap justify-center">
        <p className="text-sm text-gray-500 mr-2">Popular:</p>
        {popularSearches.slice(0, 4).map((term, index) => (
          <button
            key={index}
            type="button"
            onClick={() => {
              setQuery(term);
              onSearch(term);
            }}
            className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600 mr-2 mb-2 transition"
          >
            {term}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SearchBar;